// frontend/src/pages/AssignmentsPage.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Select, Card, Transfer, Button, Typography, Table, Space, Tabs, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined, TeamOutlined, SolutionOutlined } from '@ant-design/icons';
import { getTeachers } from '../services/userService';
import { getAllStudents } from '../services/studentService'; // Import student service
import { getAllSubjects } from '../services/subjectService';
import { getAllClasses } from '../services/classService';
import { useNotification } from '../context/NotificationContext';
import {
    getTeacherSubjectAssignments,
    saveTeacherSubjectAssignments,
    getClassAssignments,
    saveClassAssignments,
    getTutorGroup, // Import new tutor group services
    saveTutorGroup
} from '../services/assignmentService';

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const AssignmentsPage = () => {
    const { message } = useNotification();

    // --- Master Data State ---
    const [teachers, setTeachers] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [classes, setClasses] = useState([]);
    const [allStudents, setAllStudents] = useState([]); // State for all students
    const [loadingMaster, setLoadingMaster] = useState(true);
    const [teacherSubjectsMap, setTeacherSubjectsMap] = useState({});

    // --- Teacher-Subject Assignment State (Tab 1) ---
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [targetSubjectKeys, setTargetSubjectKeys] = useState([]);
    const [savingSubjects, setSavingSubjects] = useState(false);
    const [loadingTeacherSubjects, setLoadingTeacherSubjects] = useState(false);

    // --- Class-Assignment State (Tab 2) ---
    const [selectedClass, setSelectedClass] = useState(null);
    const [classAssignments, setClassAssignments] = useState([]);
    const [savingClass, setSavingClass] = useState(false);
    const [loadingClassAssignments, setLoadingClassAssignments] = useState(false);

    // --- Tutor Group State (Tab 3) ---
    const [selectedTutor, setSelectedTutor] = useState(null);
    const [targetStudentKeys, setTargetStudentKeys] = useState([]);
    const [savingTutorGroup, setSavingTutorGroup] = useState(false);
    const [loadingTutorGroup, setLoadingTutorGroup] = useState(false);
    const [selectedClassFilter, setSelectedClassFilter] = useState(null);

    // =========================================================
    // 1. INITIAL MASTER DATA FETCH
    // =========================================================
    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingMaster(true);
            try {
                const [teachersData, subjectsData, classesData, studentsData] = await Promise.all([
                    getTeachers(),
                    getAllSubjects(),
                    getAllClasses(),
                    getAllStudents()
                ]);
                setTeachers(teachersData);
                setSubjects(subjectsData.map(s => ({ ...s, key: s.id.toString() })));
                setClasses(classesData);
                setAllStudents(studentsData.map(s => ({ ...s, key: s.id.toString(), title: `${s.student_name} (${s.admission_no || s.id})` })));

                const assignmentsPromises = teachersData.map(teacher => getTeacherSubjectAssignments(teacher.id));
                const allAssignments = await Promise.all(assignmentsPromises);

                const tsm = {};
                teachersData.forEach((teacher, index) => {
                    tsm[teacher.id] = allAssignments[index].map(id => id.toString());
                });
                setTeacherSubjectsMap(tsm);

            } catch (error) {
                console.error("Master data fetch error:", error);
                message.error('Failed to load initial configuration data.');
            } finally {
                setLoadingMaster(false);
            }
        };
        fetchAllData();
    }, []);

    // =========================================================
    // 2. TEACHER SUBJECT ASSIGNMENT LOGIC (Tab 1)
    // =========================================================

    const fetchTeacherSubjects = useCallback(async (teacherId) => {
        if (!teacherId) { setTargetSubjectKeys([]); return; }
        setLoadingTeacherSubjects(true);
        try {
            // Use the data already in the map (loaded during master fetch)
            const assignedIds = teacherSubjectsMap[teacherId] || [];
            setTargetSubjectKeys(assignedIds);
        } catch (error) {
            message.error("Failed to load teacher's assigned subjects.");
            setTargetSubjectKeys([]);
        } finally {
            setLoadingTeacherSubjects(false);
        }
    }, [teacherSubjectsMap]);

    const handleTutorSelect = async (teacherId) => {
        setSelectedTutor(teacherId);
        if (!teacherId) { setTargetStudentKeys([]); return; }
        setLoadingTutorGroup(true);
        try {
            const studentIds = await getTutorGroup(teacherId);
            setTargetStudentKeys(studentIds.map(id => id.toString()));
        } catch (error) { message.error("Failed to load tutor group."); }
        finally { setLoadingTutorGroup(false); }
    };

    const handleSaveTutorGroup = async () => {
        if (!selectedTutor) return;
        setSavingTutorGroup(true);
        try {
            await saveTutorGroup(selectedTutor, targetStudentKeys.map(id => parseInt(id)));
            message.success("Tutor group saved successfully!");
        } catch (error) { message.error(error.response?.data?.error || "Failed to save tutor group."); }
        finally { setSavingTutorGroup(false); }
    };

    const handleTeacherSelect = (teacherId) => {
        setSelectedTeacher(teacherId);
        fetchTeacherSubjects(teacherId);
    };

    const handleSaveTeacherSubjects = async () => {
        if (!selectedTeacher) return;
        setSavingSubjects(true);
        try {
            // Convert string keys back to numbers for the API
            await saveTeacherSubjectAssignments(selectedTeacher, targetSubjectKeys.map(id => parseInt(id)));
            message.success("Teacher's subjects saved successfully!");

            // Update the local map instantly
            setTeacherSubjectsMap(prev => ({
                ...prev,
                [selectedTeacher]: targetSubjectKeys
            }));

        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to save subjects.');
        } finally {
            setSavingSubjects(false);
        }
    };

    // =========================================================
    // 3. CLASS ASSIGNMENT LOGIC (Tab 2)
    // =========================================================

    const fetchClassAssignments = useCallback(async (classId) => {
        if (!classId) { setClassAssignments([]); return; }
        setLoadingClassAssignments(true);
        try {
            const assignments = await getClassAssignments(classId);
            setClassAssignments(assignments.map((a, index) => ({
                ...a,
                key: index,
                teacher_id: a.teacher_id,
                subject_id: a.subject_id,
            })));
        } catch (error) {
            message.error("Failed to load class assignments.");
            setClassAssignments([]);
        } finally {
            setLoadingClassAssignments(false);
        }
    }, []);

    const handleClassSelect = (classId) => {
        setSelectedClass(classId);
        fetchClassAssignments(classId);
    };

    const handleAssignmentChange = useCallback((key, field, value) => {
        setClassAssignments(prev => prev.map(a => {
            if (a.key === key) {
                const newA = { ...a, [field]: value };
                // If the teacher changes, clear the subject selection
                if (field === 'teacher_id' && a.teacher_id !== value) {
                    newA.subject_id = null;
                }
                return newA;
            }
            return a;
        }));
    }, []);

    const handleAddAssignmentRow = () => {
        const newKey = classAssignments.length > 0 ? Math.max(...classAssignments.map(a => a.key)) + 1 : 0;
        setClassAssignments([...classAssignments, { key: newKey, teacher_id: null, subject_id: null }]);
    };

    const handleDeleteAssignmentRow = (key) => {
        setClassAssignments(classAssignments.filter(a => a.key !== key));
    };

    const handleSaveClassAssignments = async () => {
        if (!selectedClass) return;

        // Filter out incomplete rows before saving
        const validAssignments = classAssignments.filter(a => a.teacher_id && a.subject_id);

        if (validAssignments.length === 0 && classAssignments.length > 0) {
            message.warning("Only valid Teacher-Subject pairs were saved. Please ensure both fields are selected.");
        }

        setSavingClass(true);
        try {
            await saveClassAssignments(selectedClass, validAssignments);
            message.success('Class assignments saved successfully!');
            fetchClassAssignments(selectedClass);
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to save class assignments.');
        } finally {
            setSavingClass(false);
        }
    };

    // --- Columns for the Class Assignment Table ---
    const assignmentColumns = useMemo(() => [
        {
            title: 'Teacher',
            dataIndex: 'teacher_id',
            key: 'teacher_id',
            width: '40%',
            render: (teacher_id, record) => (
                <Select
                    showSearch
                    placeholder="Select teacher"
                    value={teacher_id}
                    style={{ width: '100%' }}
                    onChange={value => handleAssignmentChange(record.key, 'teacher_id', value)}
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    loading={loadingMaster}
                >
                    {teachers.map(t => <Option key={t.id} value={t.id}>{t.username}</Option>)}
                </Select>
            ),
        },
        {
            title: 'Subject',
            dataIndex: 'subject_id',
            key: 'subject_id',
            width: '40%',
            render: (subject_id, record) => {
                const teacherId = record.teacher_id;
                // Get the list of allowed subject IDs (strings) for the teacher
                const allowedSubjectIds = teacherSubjectsMap[teacherId] || [];

                const subjectOptions = subjects.filter(subject =>
                    // Match subject ID (string) from master list against allowed IDs (string array)
                    allowedSubjectIds.includes(subject.id.toString())
                );

                return (
                    <Select
                        placeholder={teacherId ? "Select subject" : "Select teacher first"}
                        value={subject_id}
                        style={{ width: '100%' }}
                        onChange={value => handleAssignmentChange(record.key, 'subject_id', value)}
                        disabled={!teacherId}
                    >
                        {subjectOptions.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                    </Select>
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            width: '20%',
            render: (_, record) => (
                <Button icon={<DeleteOutlined />} danger onClick={() => handleDeleteAssignmentRow(record.key)} />
            ),
        },
    ], [teachers, subjects, loadingMaster, teacherSubjectsMap, handleAssignmentChange]);




    // =========================================================
    // 4. RENDERING TAB COMPONENTS
    // =========================================================

    const renderTeacherSubjectTab = () => (
        <Card title="Assign Subject Responsibilities" loading={loadingMaster} style={{ minHeight: 450 }}>
            <Alert
                message="Teacher Subject Setup"
                description="Use this section to define which subjects each teacher is qualified to teach. This list dictates the subjects available when assigning them to a specific class."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <Select
                showSearch
                placeholder="Select a teacher"
                style={{ width: '100%', marginBottom: 16 }}
                onChange={handleTeacherSelect}
                disabled={loadingMaster}
            >
                {teachers.map(t => <Option key={t.id} value={t.id}>{t.username}</Option>)}
            </Select>

            {selectedTeacher && (
                <>
                    <Transfer
                        dataSource={subjects}
                        targetKeys={targetSubjectKeys}
                        onChange={setTargetSubjectKeys}
                        render={item => item.name}
                        listStyle={{ width: '100%', height: 400 }}
                        disabled={loadingTeacherSubjects}
                        titles={['Available Subjects', 'Assigned Subjects']}
                    />
                    <Button
                        type="default"
                        style={{ marginTop: 16 }}
                        onClick={handleSaveTeacherSubjects}
                        loading={savingSubjects || loadingTeacherSubjects}
                        block
                    >
                        Save Subjects for {teachers.find(t => t.id === selectedTeacher)?.username}
                    </Button>
                </>
            )}
        </Card>
    );

    const renderClassAssignmentTab = () => (
        <Card title="Assign Subject Teachers to Classes" loading={loadingMaster} style={{ minHeight: 450 }}>
            <Alert
                message="Class Assignment Setup"
                description="Select a class and assign a teacher for each subject. Teachers can only be assigned subjects they were authorized for in the 'Teacher Subject Responsibility' tab."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <Select
                placeholder="Select a class to configure"
                style={{ width: '100%', marginBottom: 16 }}
                onChange={handleClassSelect}
                disabled={loadingMaster}
            >
                {classes.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>

            {selectedClass && (
                <>
                    <Table
                        dataSource={classAssignments}
                        columns={assignmentColumns}
                        pagination={false}
                        bordered
                        loading={loadingClassAssignments}
                        scroll={{ x: 500 }}
                        locale={{ emptyText: "Select Add Assignment below to start configuring." }}
                    />
                    <Space
                        style={{
                            marginTop: 16,
                            width: '100%',
                            justifyContent: 'space-between',
                        }}
                        wrap
                    >
                        <Button
                            icon={<PlusOutlined />}
                            onClick={handleAddAssignmentRow}
                            disabled={loadingClassAssignments}
                        >
                            Add Assignment
                        </Button>
                        <Button
                            type="default"
                            onClick={handleSaveClassAssignments}
                            loading={savingClass}
                        >
                            Save Assignments for {classes.find(c => c.id === selectedClass)?.name}
                        </Button>
                    </Space>
                </>
            )}
        </Card>
    );

    const renderTutorGroupTab = () => {
        const filteredStudents = selectedClassFilter
            ? allStudents.filter(student => student.class_id === selectedClassFilter)
            : allStudents;

        return (
            <Card title="Assign Students to a Tutor/Mentor" loading={loadingMaster}>
                <Alert message="Tutor Group Setup" description="Select a teacher and assign a specific group of students they will mentor. This gives the teacher special permissions to view and manage these students' records." type="info" showIcon style={{ marginBottom: 16 }} />
                <Space style={{ width: '100%', marginBottom: 16 }} wrap>
                    <Select showSearch placeholder="Select a Tutor" style={{ minWidth: 250, flexGrow: 1 }} onChange={handleTutorSelect}>
                        {teachers.map(t => <Option key={t.id} value={t.id}>{t.username}</Option>)}
                    </Select>
                    <Select
                        allowClear
                        showSearch
                        placeholder="Filter students by class"
                        style={{ minWidth: 250, flexGrow: 1 }}
                        onChange={setSelectedClassFilter}
                        value={selectedClassFilter}
                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                        {classes.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                    </Select>
                </Space>
                {selectedTutor && (
                    <>
                        <Transfer
                            dataSource={filteredStudents}
                            targetKeys={targetStudentKeys}
                            onChange={setTargetStudentKeys}
                            render={item => item.title}
                            listStyle={{ width: '100%', height: 400 }}
                            disabled={loadingTutorGroup}
                            titles={['All Students', `Students in ${teachers.find(t => t.id === selectedTutor)?.username}'s Group`]}
                            showSearch
                            filterOption={(inputValue, item) => item.title.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1}
                        />
                        <Button type="primary" style={{ marginTop: 16 }} onClick={handleSaveTutorGroup} loading={savingTutorGroup} block>
                            Save Tutor Group
                        </Button>
                    </>
                )}
            </Card>
        );
    };



    return (
        <div>
            <Title level={2} style={{ marginBottom: '16px' }}>Assignment Management</Title>
            <Tabs defaultActiveKey="teacher_subjects" type="line" size="large">
                <TabPane tab={<span><UserOutlined /> Teacher Subjects</span>} key="teacher_subjects">
                    {renderTeacherSubjectTab()}
                </TabPane>
                <TabPane tab={<span><TeamOutlined /> Class Assignments</span>} key="class_assignments">
                    {renderClassAssignmentTab()}
                </TabPane>
                <TabPane tab={<span><SolutionOutlined /> Tutor Groups</span>} key="tutor_groups">
                    {renderTutorGroupTab()}
                </TabPane>
            </Tabs>
        </div>
    );

};

export default AssignmentsPage;